'use client'

import { Treemap, ResponsiveContainer, Tooltip } from 'recharts'
import type { TreemapPayload, TreemapNode } from '../svs-types'
import { resolveColor } from '../color-mapping'

interface TreemapRendererProps {
  payload: TreemapPayload
}

interface FlatNode {
  name: string
  size: number
  fill: string
}

function flattenTree(node: TreemapNode, depth: number, maxDepth: number, colorIdx: { current: number }): FlatNode[] {
  if (maxDepth > 0 && depth >= maxDepth) return []

  if (!node.children || node.children.length === 0) {
    return [{
      name: node.label,
      size: node.value || 0,
      fill: resolveColor(node.color, colorIdx.current++),
    }]
  }

  return node.children.flatMap(child => flattenTree(child, depth + 1, maxDepth, colorIdx))
}

/**
 * Custom content renderer for treemap cells.
 * Must be a component (not inline function) for Recharts typing.
 */
function TreemapCell(props: Record<string, unknown>) {
  const x = Number(props.x) || 0
  const y = Number(props.y) || 0
  const width = Number(props.width) || 0
  const height = Number(props.height) || 0
  const name = String(props.name || '')
  const fill = String(props.fill || '#888')

  if (width < 4 || height < 4) return null

  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={fill} rx={2} opacity={0.85} stroke="hsl(var(--background))" strokeWidth={2} />
      {width > 50 && height > 30 && (
        <text
          x={x + width / 2}
          y={y + height / 2}
          textAnchor="middle"
          dominantBaseline="central"
          fill="white"
          style={{ fontSize: Math.min(12, width / 8), fontWeight: 500 }}
        >
          {name}
        </text>
      )}
    </g>
  )
}

export function TreemapRenderer({ payload }: TreemapRendererProps) {
  const options = payload.options || {}
  const maxDepth = options.maxDepth || 0
  const colorIdx = { current: 0 }
  const flatData = flattenTree(payload.data.root, 0, maxDepth, colorIdx)

  return (
    <ResponsiveContainer width="100%" height={300}>
      <Treemap
        data={flatData}
        dataKey="size"
        nameKey="name"
        aspectRatio={4 / 3}
        stroke="hsl(var(--border))"
        content={<TreemapCell />}
      >
        <Tooltip formatter={(value: number) => value.toLocaleString()} />
      </Treemap>
    </ResponsiveContainer>
  )
}
